import {createSlice, Dictionary, PayloadAction} from '@reduxjs/toolkit'
import Action from 'src/models/Action'
import {takeEvery, getContext, call, put} from 'typed-redux-saga'
import Api from '../../api/reactpt'
import {DataState} from '../../types/dataState.type'
import _ from 'lodash'
import {shouldLogUserOut} from '../configureStore'

export type ActionsState = {
  actions: Dictionary<Action>
  dataState: DataState
}

const initialState: ActionsState = {
  actions: {},
  dataState: {state: 'unloaded'},
}

export const {actions, reducer} = createSlice({
  name: 'actions',
  initialState,
  reducers: {
    getAction: (_state: ActionsState, _action: PayloadAction<{id: string}>) => {},
    saveAction: (
      _state: ActionsState,
      _action: PayloadAction<{action: Action; videoFile?: File}>,
    ) => {},
    deleteAction: (_state: ActionsState, _action: PayloadAction<{actionId: string}>) => {},
    getAllActions: (_state: ActionsState, _action: PayloadAction<void>) => {},
    setActions: (state: ActionsState, action: PayloadAction<{actions: Action[]}>) => {
      const keyed = _.keyBy(action.payload.actions, ({id}) => id)
      const newActions = {...keyed} as Dictionary<Action>
      return {...state, actions: newActions}
    },
    setDataState: (state: ActionsState, action: PayloadAction<{dataState: DataState}>) => {
      const {dataState} = action.payload
      return {...state, dataState}
    },
    clear: (_state: ActionsState) => {
      return {...initialState}
    },
  },
})

export const getAllActionsSaga = function* () {
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    const result = yield* call(api.getAllActions)
    yield* put(actions.setActions({actions: result}))
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}
export const getActionSaga = function* (action: ReturnType<typeof actions.getAction>) {
  const {id} = action.payload
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    const result = yield* call(api.getAction, id)
    yield* put(actions.setActions({actions: [result]}))
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const deleteActionSaga = function* (action: ReturnType<typeof actions.deleteAction>) {
  const {actionId} = action.payload
  const api: Api = yield* getContext<Api>('api')
  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    yield* call(api.deleteAction, actionId)
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
    yield* put(actions.getAllActions())
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saveActionSaga = function* (action: ReturnType<typeof actions.saveAction>) {
  const {videoFile} = action.payload
  let updatedAction = {...action.payload.action}
  const api = yield* getContext<Api>('api')

  yield* put(actions.setDataState({dataState: {state: 'loading'}}))
  try {
    if (videoFile) {
      // There's a video file, which means we need to request an upload URL.
      const {uploadURL, id} = yield* call(api.requestVideoUpload)

      // Now we can upload the video.
      yield* call(api.uploadVideo, uploadURL, videoFile)

      // Now that the video is uploaded, we can get the appropriate URLs (needed
      // to stream the video but also for the thumbnail)
      const {videoUrl, thumbnailUrl} = yield* call(api.requestVideoUploadInfo, id)

      // Save the URLs.
      updatedAction.videoUrl = videoUrl
      updatedAction.thumbnailUrl = thumbnailUrl
    }
    let result: Action
    if (updatedAction.id) {
      result = yield* call(api.updateAction, updatedAction)
    } else {
      result = yield* call(api.createAction, updatedAction)
    }
    yield* put(actions.setActions({actions: [result]}))
    yield* put(actions.setDataState({dataState: {state: 'loaded'}}))
  } catch (error) {
    if (shouldLogUserOut(error)) {
      throw error
    } else {
      yield* put(actions.setDataState({dataState: {state: 'unloaded', error}}))
    }
  }
}

export const saga = function* () {
  yield* takeEvery(actions.getAllActions.type, getAllActionsSaga)
  yield* takeEvery(actions.getAction.type, getActionSaga)
  yield* takeEvery(actions.saveAction.type, saveActionSaga)
  yield* takeEvery(actions.deleteAction.type, deleteActionSaga)
}
